import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { isWebhookHealthy } from '../../lib/inboxView';
import { fetchWebhookHealth, registerWebhook, testWebhook } from './api';

// Community Inbox — admin webhook health card (Phase 0c health surface).
// Compact status of the Zernio engagement-webhook registration: a dead or
// missing webhook silently stops the inbox (Zernio auto-disables after 10
// consecutive delivery failures).
//
// PLACEMENT (Part F, 2026-07-19): this lives INSIDE the Manage-accounts dialog
// rather than as a permanent page row. Because hiding a silent-failure warning
// is risky, the Manage accounts BUTTON carries a small amber dot when unhealthy
// (see useWebhookHealthState) — the warning is still discoverable, it just
// doesn't cost a row.

/**
 * Shared webhook-health read. Same query key as the card, so the button
 * indicator and the card are ONE fetch.
 */
export function useWebhookHealthState(): { loading: boolean; healthy: boolean } {
  const health = useQuery({
    queryKey: ['community-webhook-health'],
    queryFn: fetchWebhookHealth,
    refetchInterval: 60_000,
  });
  return {
    loading: health.isLoading,
    healthy: health.isError ? true : isWebhookHealthy(health.data),
  };
}

export function WebhookHealthCard() {
  const queryClient = useQueryClient();
  const health = useQuery({
    queryKey: ['community-webhook-health'],
    queryFn: fetchWebhookHealth,
    refetchInterval: 60_000,
  });

  const register = useMutation({
    mutationFn: registerWebhook,
    onSuccess: (res) => {
      toast.success(res.created ? 'Webhook registered.' : 'Webhook registration updated.');
      void queryClient.invalidateQueries({ queryKey: ['community-webhook-health'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const test = useMutation({
    mutationFn: testWebhook,
    onSuccess: () => toast.success('Test delivery triggered — check the delivery logs.'),
    onError: (err: Error) => toast.error(err.message),
  });

  if (health.isLoading || health.isError || !health.data) return null;
  const h = health.data;
  const healthy = isWebhookHealthy(h);

  return (
    <div
      className={
        healthy
          ? 'flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600'
          : 'flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800'
      }
    >
      {healthy ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-kyck-600" />
      ) : (
        <AlertTriangle className="h-4 w-4 shrink-0" />
      )}
      <span className="min-w-0 flex-1">
        {!h.secretConfigured
          ? 'Webhook signing secret not configured — provision ZERNIO_WEBHOOK_SIGNING_KEY.'
          : !h.registered
            ? 'Zernio webhook is not registered — new comments will not arrive.'
            : h.isActive === false
              ? 'Zernio webhook is DISABLED (too many delivery failures) — re-register to reactivate.'
              : (h.failureCount ?? 0) > 0
                ? `Webhook active with ${h.failureCount} recent delivery failure(s).`
                : `Webhook active · last delivery ${h.lastFiredAt ? new Date(h.lastFiredAt).toLocaleString() : 'never'}`}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => register.mutate()}
        disabled={register.isPending || !h.secretConfigured}
      >
        {h.registered ? 'Re-register' : 'Register'}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => test.mutate()}
        disabled={test.isPending || !h.registered}
      >
        Send test
      </Button>
    </div>
  );
}
